/**
 * TestPromptModal Component (UI-3)
 * Modal for testing Q AI prompts with sample messages
 *
 * Features:
 * - Test message input field
 * - User type radio: Connected / Disconnected
 * - Options checkboxes: Include signals, Include strategies
 * - "Run Test" button
 * - Generated System Prompt display (expandable, copyable)
 * - Q AI Response display area
 * - Result stats: Response time, tokens used
 */

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Checkbox,
    IconButton,
    Tooltip,
    Collapse,
    Alert,
    LinearProgress,
    Paper,
    Chip,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TimerIcon from '@mui/icons-material/Timer';
import TokenIcon from '@mui/icons-material/Token';
import { toast } from 'react-toastify';

import { testPrompt } from '../../api/promptsApi';

// Embedded theme
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539', quaternary: '#3f4147' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.85)', quaternary: 'rgba(255,255,255,0.75)', placeholder: 'rgba(255,255,255,0.6)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)', tertiary: 'rgba(255,255,255,0.15)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    borderRadius: { sm: '8px', md: '12px' }
};

const TestPromptModal = ({ open, prompt, onClose }) => {
    // Form state
    const [testMessage, setTestMessage] = useState('What\'s your view on EURUSD right now?');
    const [userType, setUserType] = useState('connected');
    const [includeSignals, setIncludeSignals] = useState(true);
    const [includeStrategies, setIncludeStrategies] = useState(true);

    // Result state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [generatedPromptExpanded, setGeneratedPromptExpanded] = useState(false);

    // ─────────────────────────────────────────────────────────────────────────
    // Run Test
    // ─────────────────────────────────────────────────────────────────────────

    const handleRunTest = async () => {
        if (!testMessage.trim()) {
            toast.error('Please enter a test message');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        const testData = {
            promptKey: prompt?.promptKey,
            testMessage: testMessage.trim(),
            simulateUserType: userType,
            includeMarketSignals: includeSignals,
            includeStrategies: includeStrategies
        };

        const response = await testPrompt(testData);

        setLoading(false);

        if (response.success) {
            setResult(response.data);
        } else {
            setError(response.error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Copy to clipboard
    // ─────────────────────────────────────────────────────────────────────────

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Reset on close
    // ─────────────────────────────────────────────────────────────────────────

    const handleClose = () => {
        setResult(null);
        setError(null);
        onClose();
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Text field styles
    // ─────────────────────────────────────────────────────────────────────────

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            background: theme.colors.bg.tertiary,
            color: theme.colors.text.primary,
            '& fieldset': { borderColor: theme.colors.border.primary },
            '&:hover fieldset': { borderColor: theme.colors.border.tertiary },
            '&.Mui-focused fieldset': { borderColor: theme.colors.accent.primary }
        },
        '& .MuiInputLabel-root': { color: theme.colors.text.quaternary },
        '& .MuiInputLabel-root.Mui-focused': { color: theme.colors.accent.tertiary }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: theme.colors.bg.secondary,
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.border.primary}`,
                    maxHeight: '90vh'
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.colors.border.primary}`,
                    pb: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: theme.borderRadius.sm,
                            background: `${theme.colors.status.info}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <PlayArrowIcon sx={{ color: theme.colors.status.info }} />
                    </Box>
                    <Box>
                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, fontSize: '1.1rem' }}>
                            Test Prompt
                        </Typography>
                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                            {prompt?.promptName || 'Unknown'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: theme.colors.text.quaternary }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Test Message */}
                <TextField
                    label="Test Message"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Enter a test message to send to Q AI..."
                    sx={textFieldSx}
                />

                {/* Options Row */}
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {/* User Type */}
                    <FormControl>
                        <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, mb: 1, fontSize: '0.875rem' }}>
                            Simulate User Type
                        </Typography>
                        <RadioGroup
                            value={userType}
                            onChange={(e) => setUserType(e.target.value)}
                            row
                        >
                            <FormControlLabel
                                value="connected"
                                control={<Radio sx={{ color: theme.colors.text.quaternary, '&.Mui-checked': { color: theme.colors.accent.primary } }} />}
                                label={<Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.875rem' }}>Connected (has trades)</Typography>}
                            />
                            <FormControlLabel
                                value="disconnected"
                                control={<Radio sx={{ color: theme.colors.text.quaternary, '&.Mui-checked': { color: theme.colors.accent.primary } }} />}
                                label={<Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.875rem' }}>Disconnected (no QBridge)</Typography>}
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Include Options */}
                    <FormControl>
                        <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, mb: 1, fontSize: '0.875rem' }}>
                            Include Sample Data
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeSignals}
                                        onChange={(e) => setIncludeSignals(e.target.checked)}
                                        sx={{ color: theme.colors.text.quaternary, '&.Mui-checked': { color: theme.colors.accent.primary } }}
                                    />
                                }
                                label={<Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.875rem' }}>Market Signals</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeStrategies}
                                        onChange={(e) => setIncludeStrategies(e.target.checked)}
                                        sx={{ color: theme.colors.text.quaternary, '&.Mui-checked': { color: theme.colors.accent.primary } }}
                                    />
                                }
                                label={<Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.875rem' }}>Strategy Performance</Typography>}
                            />
                        </Box>
                    </FormControl>
                </Box>

                {/* Run Test Button */}
                <Button
                    variant="contained"
                    onClick={handleRunTest}
                    disabled={loading || !testMessage.trim()}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                    sx={{
                        py: 1.5,
                        background: theme.colors.status.info,
                        '&:hover': { background: '#2563eb' },
                        '&.Mui-disabled': { background: theme.colors.bg.quaternary }
                    }}
                >
                    {loading ? 'Running Test...' : 'Run Test'}
                </Button>

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ background: `${theme.colors.status.error}20`, color: theme.colors.status.error }}
                        icon={<ErrorIcon />}
                    >
                        {error}
                    </Alert>
                )}

                {/* Results */}
                {result && (
                    <>
                        {/* Divider */}
                        <Box sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, pt: 2 }} />

                        {/* Generated System Prompt */}
                        <Paper
                            sx={{
                                background: theme.colors.bg.tertiary,
                                border: `1px solid ${theme.colors.border.primary}`,
                                borderRadius: theme.borderRadius.sm,
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    background: theme.colors.bg.quaternary,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setGeneratedPromptExpanded(!generatedPromptExpanded)}
                            >
                                <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, fontSize: '0.875rem' }}>
                                    Generated System Prompt
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tooltip title="Copy">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(result.generatedPrompt, 'System prompt'); }}
                                            sx={{ color: theme.colors.text.quaternary }}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton size="small" sx={{ color: theme.colors.text.quaternary }}>
                                        {generatedPromptExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>
                            </Box>
                            <Collapse in={generatedPromptExpanded}>
                                <Box
                                    sx={{
                                        p: 2,
                                        maxHeight: 200,
                                        overflow: 'auto',
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        color: theme.colors.text.tertiary,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {result.generatedPrompt || 'No system prompt generated'}
                                </Box>
                            </Collapse>
                        </Paper>

                        {/* Q AI Response */}
                        <Paper
                            sx={{
                                background: theme.colors.bg.tertiary,
                                border: `1px solid ${theme.colors.border.primary}`,
                                borderRadius: theme.borderRadius.sm,
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    background: `${theme.colors.status.success}15`,
                                    borderBottom: `1px solid ${theme.colors.border.primary}`
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '6px',
                                            background: 'linear-gradient(135deg, #27B56D 0%, #22C55E 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.7rem' }}>Q</Typography>
                                    </Box>
                                    <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, fontSize: '0.875rem' }}>
                                        Q AI Response
                                    </Typography>
                                </Box>
                                <Tooltip title="Copy response">
                                    <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(result.response, 'Response')}
                                        sx={{ color: theme.colors.text.quaternary }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box
                                sx={{
                                    p: 2,
                                    maxHeight: 300,
                                    overflow: 'auto',
                                    color: theme.colors.text.primary,
                                    fontSize: '0.9rem',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap'
                                }}
                            >
                                {result.response || 'No response received'}
                            </Box>
                        </Paper>

                        {/* Stats */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                p: 2,
                                background: `${theme.colors.status.success}10`,
                                borderRadius: theme.borderRadius.sm,
                                border: `1px solid ${theme.colors.status.success}30`
                            }}
                        >
                            <CheckCircleIcon sx={{ color: theme.colors.status.success }} />
                            <Box sx={{ display: 'flex', gap: 3, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TimerIcon sx={{ fontSize: 18, color: theme.colors.text.quaternary }} />
                                    <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.875rem' }}>
                                        {result.responseTimeMs ? `${(result.responseTimeMs / 1000).toFixed(2)}s` : 'N/A'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TokenIcon sx={{ fontSize: 18, color: theme.colors.text.quaternary }} />
                                    <Typography sx={{ color: theme.colors.text.tertiary, fontSize: '0.875rem' }}>
                                        {result.tokensUsed ?? 'N/A'} tokens used
                                    </Typography>
                                </Box>
                                {result.response && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                            {result.response.split(/\s+/).length} words
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TestPromptModal;
